import React, { useState, useEffect, useContext } from 'react';
import api, { clearStoredSession, getUserFacingErrorMessage, isAuthError } from '../utils/api';
import { UserContext } from '../App';
import WillForm from './WillForm';
import UploadSignedWill from './UploadSignedWill';
import WillInformation from './WillInformation';
import ContextSwitcher from './ContextSwitcher';
import NomineeRequiredBanner from './NomineeRequiredBanner';
import OwnerShell from './OwnerShell';
import ResponsiveOptionNav from './ResponsiveOptionNav';
import { Button, Modal } from '../ui';

function BeneficiaryWillSummary({ ownerId }) {
  const [current, setCurrent] = React.useState(null);
  React.useEffect(() => {
    // For nominees, only display filename (non-clickable); query with owner's id via header override
    let cancelled = false;
    api.get('/will/list', { headers: { 'X-User-Id': ownerId } })
      .then(res => {
        if (cancelled) return;
        setCurrent(res.data.currentFile || null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('BeneficiaryWillSummary: failed to load will metadata', err?.response?.data || err);
        setCurrent(null);
      });
    return () => { cancelled = true; };
  }, [ownerId]);
  return (
    <div>
      {!current && (<div style={{ color: '#666' }}>No will on file.</div>)}
      {current && (
        <div style={{ color: '#222' }}>{current.fileName}</div>
      )}
      <div style={{ fontSize: 13, color: '#777', marginTop: 6 }}>Beneficiaries cannot view or download the will.</div>
    </div>
  );
}
import { useNavigate, useLocation } from 'react-router-dom';

const LEGAL_CATEGORIES = [
  'Will',
  'Other Legal Document'
];

// Frontend scope (temporary): LPA and pre-nuptial are not shown as first-class sections.
// - LPA is fully hidden from the UI.
// - Pre-nuptial is bucketed under "Other Legal Document" so legacy rows remain visible.
const HIDDEN_SOURCE_CATEGORIES = new Set(['Lasting Power of Attorney']);
function normalizeLegalCategoryForUi(typeCategory) {
  return typeCategory === 'Pre-nuptial Agreement' ? 'Other Legal Document' : typeCategory;
}

const TYPE_OPTIONS = {
  'Will': ['Last Will and Testament', 'Codicil'],
  'Other Legal Document': ['Trust Deed', 'Power of Attorney', 'Living Will', 'Other']
};

export default function LegalAssets() {
  const { currentUser, users, setCurrentUser } = useContext(UserContext);
  const [assets, setAssets] = useState([]);
  const [hasNominees, setHasNominees] = useState(null);
  const [willTab, setWillTab] = useState('info'); // default to Will Information
  const [modalOpen, setModalOpen] = useState(false);
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [form, setForm] = useState({ type: '', typeCategory: '', institution: '', accountNo: '', value: '', notes: '' });
  const [activeCategory, setActiveCategory] = useState('Will');
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [step, setStep] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  // Ensure default selection to Will tab and Upload sub-tab on mount
  useEffect(() => {
    setActiveCategory('Will');
    setWillTab('info');
  }, []);


  // Asset owner is the current logged-in user
  const assetHolder = currentUser;
  if (!assetHolder) {
    return <div>Loading...</div>;
  }

  // Nominee journey now lives under /nominee.
  useEffect(() => {
    if (currentUser?.type === 'nominee') {
      navigate('/nominee');
    }
  }, [currentUser?.type, navigate]);

  if (currentUser?.type === 'nominee') {
    return <div style={{ padding: 20 }}>Redirecting…</div>;
  }

  // Fetch nominee presence (owner-scoped) to decide whether to allow asset/will mutations.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/nominees');
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : [];
        setHasNominees(list.length > 0);
      } catch (err) {
        if (cancelled) return;
        console.error('LegalAssets: failed to load nominees', err?.response?.data || err);
        if (isAuthError(err)) {
          clearStoredSession();
          setCurrentUser(null);
        } else {
          setHasNominees(null);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [setCurrentUser]);

  // Fetch legal assets for asset holder (owner view)
  useEffect(() => {
    if (currentUser.type === 'owner') {
      let cancelled = false;
      (async () => {
        try {
          const res = await api.get('/assets');
          if (cancelled) return;
          const all = Array.isArray(res.data) ? res.data : [];
          // Hide categories that are currently out of scope for the frontend.
          const holderAssets = all
            .filter(a => a.ownerId === assetHolder.id)
            .filter(a => !HIDDEN_SOURCE_CATEGORIES.has(a.typeCategory))
            .filter(a => LEGAL_CATEGORIES.includes(normalizeLegalCategoryForUi(a.typeCategory)));
          setAssets(holderAssets);
          if (holderAssets.length > 0) {
            const firstCat = LEGAL_CATEGORIES.find(cat => holderAssets.some(a => normalizeLegalCategoryForUi(a.typeCategory) === cat));
            setActiveCategory(firstCat || LEGAL_CATEGORIES[0]);
          } else {
            setActiveCategory(LEGAL_CATEGORIES[0]);
          }
        } catch (err) {
          if (cancelled) return;
          console.error('LegalAssets: failed to load assets', err?.response?.data || err);
          if (isAuthError(err)) {
            clearStoredSession();
            setCurrentUser(null);
          }
        }
      })();
      return () => { cancelled = true; };
    }
  }, [currentUser, assetHolder.id]);

  // Note: nominee views were removed from this screen in favour of /nominee/* pages.

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  // Step logic
  const resetForm = () => {
    setForm({ type: '', typeCategory: '', institution: '', accountNo: '', value: '', notes: '' });
    setStep(0);
    setEditingAssetId(null);
  };

  const refreshAssets = async () => {
    const res = await api.get('/assets');
    const all = Array.isArray(res.data) ? res.data : [];
    // Same filtering/mapping rules as the initial load.
    setAssets(
      all
        .filter(a => a.ownerId === assetHolder.id)
        .filter(a => !HIDDEN_SOURCE_CATEGORIES.has(a.typeCategory))
        .filter(a => LEGAL_CATEGORIES.includes(normalizeLegalCategoryForUi(a.typeCategory)))
    );
  };

  const openModal = (assetToEdit = null) => {
    if (hasNominees === false) {
      navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
      return;
    }
    setModalOpen(true);
    if (assetToEdit) {
      setEditingAssetId(assetToEdit.id);
      setForm({
        type: assetToEdit.type || '',
        // Normalize legacy categories into the UI buckets.
        typeCategory: normalizeLegalCategoryForUi(assetToEdit.typeCategory || ''),
        institution: assetToEdit.institution || '',
        accountNo: assetToEdit.accountNo || '',
        value: assetToEdit.value == null ? '' : String(assetToEdit.value),
        notes: assetToEdit.notes || '',
      });
      setStep(3);
    } else {
      resetForm();
    }
  };
  const closeModal = () => { setModalOpen(false); resetForm(); };

  const handleCategorySelect = e => {
    setForm(f => ({ ...f, typeCategory: e.target.value, type: '', institution: '' }));
    setStep(1);
  };
  const handleTypeSelect = e => {
    setForm(f => ({ ...f, type: e.target.value }));
    // Keep details on a single screen; auto-advancing while typing is poor UX.
    setStep(3);
  };
  const handleInstitution = e => {
    setForm(f => ({ ...f, institution: e.target.value }));
  };
  const handleAccountNo = e => setForm(f => ({ ...f, accountNo: e.target.value }));
  const handleValue = e => setForm(f => ({ ...f, value: e.target.value }));

  const saveAsset = async e => {
    e.preventDefault();
    try {
      const payload = { ...form, ownerId: assetHolder.id, notes: form.notes || '' };
      if (editingAssetId) {
        await api.patch(`/assets/${encodeURIComponent(editingAssetId)}`, payload);
      } else {
        await api.post('/assets', payload);
      }
      await refreshAssets();
      closeModal();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (err?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(err, {
          fallback: 'Add at least one nominee before you can add legal documents.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
        return;
      }
      console.error('LegalAssets save failed:', err?.response?.data || err);
      alert(getUserFacingErrorMessage(err, {
        fallback: 'We could not save that document. Check your entries and try again.',
        includeSupportForUnknown: false,
      }));
    }
  };

  const deleteAsset = async (asset) => {
    if (!asset?.id) return;
    const ok = window.confirm('Delete this document? This cannot be undone.');
    if (!ok) return;
    try {
      await api.delete(`/assets/${encodeURIComponent(asset.id)}`);
      await refreshAssets();
    } catch (err) {
      const code = err?.response?.data?.code;
      if (err?.response?.status === 409 && code === 'NOMINEE_REQUIRED') {
        alert(getUserFacingErrorMessage(err, {
          fallback: 'Add at least one nominee before you can change legal documents.',
          includeSupportForUnknown: false,
        }));
        navigate('/account?tab=nominees', { state: { tab: 'nominees', returnTo: location.pathname } });
        return;
      }
      alert(getUserFacingErrorMessage(err, {
        fallback: 'We could not delete that document. Please try again.',
        includeSupportForUnknown: false,
      }));
    }
  };

  return (
    <OwnerShell>
      <>
        <div style={{ padding: 20, position: 'relative' }}>
          {currentUser.type === 'owner' && hasNominees === false && (
            <NomineeRequiredBanner returnTo={location.pathname} />
          )}
          {currentUser.type === 'nominee' ? (
            <>
              {/* Owner selector dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
                <h1 style={{ margin: 0 }}>
                  {(() => {
                    const donor = beneficiaryOwners.find(o => o.ownerId === selectedOwnerId);
                    if (!donor) return 'Owner\'s Legal Documents';
                    let profile = {};
                    try { profile = JSON.parse(localStorage.getItem('ownerProfile') || '{}'); } catch {}
                    const displayName = (profile.firstName && profile.lastName)
                      ? `${profile.firstName} ${profile.lastName}`
                      : (donor.ownerName || 'Owner');
                    return `${displayName}'s Legal Documents`;
                  })()}
                </h1>
                <select
                  value={selectedOwnerId}
                  onChange={e => setSelectedOwnerId(e.target.value)}
                  style={{ padding: '8px 14px', borderRadius: 6, border: '1px solid #ccc', fontSize: 16, minWidth: 180 }}
                >
                  {beneficiaryOwners.map(o => (
                    <option key={o.ownerId} value={o.ownerId}>{(() => {
                      let profile = {};
                      try { profile = JSON.parse(localStorage.getItem('ownerProfile') || '{}'); } catch {}
                      return (profile.firstName && profile.lastName)
                        ? `${profile.firstName} ${profile.lastName}`
                        : (o.ownerName || o.ownerId);
                    })()}</option>
                  ))}
                </select>
              </div>
              {/* Category Tabs */}
              <div style={{ display: 'flex', gap: 8, marginBottom: 24, marginTop: 8, flexWrap: 'wrap' }}>
                {LEGAL_CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: 12,
                      border: 'none',
                      background: activeCategory === cat ? 'var(--sage-600)' : 'var(--mist-200)',
                      color: activeCategory === cat ? '#fff' : 'var(--dusk-700)',
                      fontWeight: activeCategory === cat ? 600 : 400,
                      fontSize: 15,
                      cursor: 'pointer',
                      boxShadow: activeCategory === cat ? 'var(--shadow-soft)' : 'none',
                      transition: 'all 0.15s',
                    }}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              {activeCategory === 'Will' ? (
                <div>
                  <div style={{ background: '#fff', border: '1px solid var(--mist-300)', borderRadius: 12, padding: 16 }}>
                    <div style={{ fontWeight: 700, marginBottom: 8 }}>Owner's Will</div>
                    <BeneficiaryWillSummary ownerId={selectedOwnerId} />
                  </div>
                </div>
              ) : beneficiaryAssets.filter(a => a.typeCategory === activeCategory).length === 0 ? (
                <div style={{ marginTop: 60, textAlign: 'center', color: '#888' }}>
                  <p style={{ fontSize: 20 }}>No legal documents in this category yet.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Type</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Institution</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Reference</th>
                      <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {beneficiaryAssets.filter(a => a.typeCategory === activeCategory).map(a => (
                      <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '10px 8px' }}>{a.type}</td>
                        <td style={{ padding: '10px 8px' }}>{a.institution}</td>
                        <td style={{ padding: '10px 8px' }}>{a.accountNo}</td>
                        <td style={{ padding: '10px 8px' }}>{a.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
                <h1 style={{ margin: 0 }}>Legal Documents</h1>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <ContextSwitcher />
                  <button
                    onClick={() => openModal()}
                    aria-disabled={hasNominees === false}
                    title={hasNominees === false ? 'Add a nominee first' : 'Add a legal document'}
                    style={{
                      padding: '10px 18px',
                      fontSize: 16,
                      background: hasNominees === false ? '#8fa8a0' : 'var(--sage-600)',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      cursor: 'pointer',
                      boxShadow: 'var(--shadow-soft)',
                      fontWeight: 600,
                      opacity: hasNominees === false ? 0.85 : 1,
                      maxWidth: '100%',
                    }}
                  >
                    Add legal document
                  </button>
                </div>
              </div>
              {/* Category Tabs */}
              <ResponsiveOptionNav
                label="Category"
                options={LEGAL_CATEGORIES}
                value={activeCategory}
                onChange={setActiveCategory}
              />
              {activeCategory === 'Will' ? (
                <div>
                  <ResponsiveOptionNav
                    label="Will"
                    mobileBreakpointPx={640}
                    options={[
                      { value: 'info', label: 'Will Information' },
                      { value: 'upload', label: 'Upload Signed Will' },
                      { value: 'create', label: 'Create Will' },
                    ]}
                    value={willTab}
                    onChange={setWillTab}
                  />
                  {willTab === 'create'
                    ? <WillForm nomineeGate={hasNominees === false} />
                    : willTab === 'upload'
                      ? <UploadSignedWill nomineeGate={hasNominees === false} />
                      : <WillInformation nomineeGate={hasNominees === false} />}
                </div>
              ) : assets.filter(a => normalizeLegalCategoryForUi(a.typeCategory) === activeCategory).length === 0 ? (
                <div style={{ marginTop: 60, textAlign: 'center', color: '#888' }}>
                  <p style={{ fontSize: 20 }}>No legal documents in this category yet.</p>
                  <p>Click <b>Add legal document</b> to get started.</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto', borderRadius: 8 }}>
                  <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse', marginTop: 20, background: '#fff', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.07)' }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Type</th>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Institution</th>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Reference</th>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Notes</th>
                        <th style={{ padding: '12px 8px', borderBottom: '1px solid #eee', textAlign: 'left' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assets.filter(a => normalizeLegalCategoryForUi(a.typeCategory) === activeCategory).map(a => (
                        <tr key={a.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '10px 8px' }}>{a.type}</td>
                          <td style={{ padding: '10px 8px' }}>{a.institution}</td>
                          <td style={{ padding: '10px 8px' }}>{a.accountNo}</td>
                          <td style={{ padding: '10px 8px' }}>{a.notes}</td>
                          <td style={{ padding: '10px 8px' }}>
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              <Button type="button" variant="primary" onClick={() => openModal(a)}>
                                Edit
                              </Button>
                              <Button type="button" variant="danger" onClick={() => deleteAsset(a)}>
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        {/* Modal for asset holder only */}
        {currentUser.type === 'owner' && (
          <Modal
            open={modalOpen}
            onClose={closeModal}
            title={editingAssetId ? 'Edit Legal Document' : 'Add Legal Document'}
            subtitle={editingAssetId ? 'Update the details of your legal document.' : 'Enter the details of your legal document below.'}
          >
            <form onSubmit={saveAsset} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {step === 0 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Document Category<br />
                    <select name="typeCategory" value={form.typeCategory} onChange={handleCategorySelect} required style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginTop: 6 }}>
                      <option value="" disabled>Select category</option>
                      {LEGAL_CATEGORIES.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                </div>
              )}
              {step === 1 && (
                <div style={{ marginBottom: 0 }}>
                  <label style={{ fontWeight: 500, color: '#333' }}>Document Type<br />
                    <select name="type" value={form.type} onChange={handleTypeSelect} required style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginTop: 6 }}>
                      <option value="" disabled>Select type</option>
                      {TYPE_OPTIONS[form.typeCategory]?.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  </label>
                  <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                    <Button type="button" variant="secondary" onClick={() => setStep(0)}>
                      Back
                    </Button>
                  </div>
                </div>
              )}
              {step === 3 && (
                <>
                  <div style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 500, color: '#333' }}>Institution / lawyer<br />
                      <input
                        name="legal_institution"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder="e.g., Law firm / solicitor / provider"
                        value={form.institution}
                        onChange={handleInstitution}
                        required
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginTop: 6 }}
                      />
                    </label>
                  </div>
                  <div style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 500, color: '#333' }}>Reference (optional)<br />
                      <input
                        name="legal_reference"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder="Document reference / ID (optional)"
                        value={form.accountNo}
                        onChange={handleAccountNo}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginTop: 6 }}
                      />
                    </label>
                  </div>
                  <div style={{ marginBottom: 0 }}>
                    <label style={{ fontWeight: 500, color: '#333' }}>Details / notes (optional)<br />
                      <textarea
                        name="legal_notes"
                        autoComplete="off"
                        autoCorrect="off"
                        spellCheck={false}
                        placeholder="Any extra details (optional)…"
                        value={form.notes}
                        onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginTop: 6, minHeight: 90, resize: 'vertical' }}
                      />
                    </label>
                  </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                    <Button type="button" variant="secondary" onClick={() => setStep(1)}>
                      Back
                    </Button>
                    <Button type="submit" variant="primary" style={{ flex: 1 }}>
                      {editingAssetId ? 'Save changes' : 'Save'}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </Modal>
        )}
      </>
    </OwnerShell>
  );
} 